<!-- src/components/forms/completed/InductionCompleted.vue -->
<template>
  <div class="space-y-12 animate-fadeIn">
    
    <!-- 1. Induction Logistics -->
    <section>
      <div class="flex items-center gap-3 mb-6">
        <div class="bg-[#20FCF1] p-2 rounded-lg shadow-sm">
          <CalendarDaysIcon class="h-5 w-5 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">1. Induction Logistics</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-8 rounded-[32px] border border-slate-100 shadow-inner">
        <div class="StatCard bg-white">
          <span class="label">Delivery Date</span>
          <span class="value">{{ formatDate(submissionData.inductionDate || submissionData.inductionDeliveredDate, 'dateOnly') }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">Delivery Location</span>
          <span class="value uppercase">{{ submissionData.inductionLocation || submissionData.inductionDeliveredLocation || 'N/A' }}</span>
        </div>
      </div>
    </section>

    <!-- 2. Compliance & Agreements -->
    <section>
      <div class="flex items-center gap-3 mb-6">
        <div class="bg-[#20FCF1] p-2 rounded-lg shadow-sm">
          <ShieldCheckIcon class="h-5 w-5 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">2. Agreements & Acknowledgements</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div v-for="item in agreements" :key="item.label" 
             class="flex items-center p-4 bg-white border border-slate-200 rounded-2xl shadow-sm">
          <div class="bg-[#EBFDF8] p-2 rounded-full mr-4">
            <CheckCircleIcon class="h-5 w-5 text-[#20FCF1]" />
          </div>
          <span class="text-[10px] font-black uppercase text-[#0A1630] tracking-tight">{{ item.label }}</span>
        </div>
      </div>
    </section>

    <!-- 3. Final Sign-Off -->
    <section class="border-t pt-12">
      <div class="flex flex-col md:flex-row justify-between items-end gap-8">
        <div class="space-y-4">
          <div class="flex items-center gap-2">
            <CheckBadgeIcon class="h-5 w-5 text-[#20FCF1]" />
            <p class="text-[10px] font-black uppercase tracking-widest text-[#0A1630]">Induction Record Verified</p>
          </div>
          <div class="p-6 bg-slate-900 rounded-[32px] border border-slate-800 max-w-sm">
            <p class="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Employee Declaration</p>
            <p class="text-xs text-slate-300 font-medium italic">"I acknowledge receipt of all company policies and procedures and confirm my responsibility to adhere to the company expectations."</p>
          </div>
        </div>

        <div v-if="submissionData.signatureDataUrl || submissionData.signatureUrl" class="bg-white p-6 rounded-[32px] border border-slate-200 shadow-xl flex flex-col items-center min-w-[260px]">
          <p class="text-[9px] font-black uppercase text-slate-400 mb-4">Digital Identity Seal</p>
          <img :src="submissionData.signatureDataUrl || submissionData.signatureUrl" class="max-h-20 object-contain mb-4" />
          <div class="w-full h-px bg-slate-100 mb-4"></div>
          <div class="flex flex-col items-center">
            <span class="text-[10px] font-black uppercase text-[#0A1630] tracking-widest">{{ submissionData.name }}</span>
            <span class="text-[9px] font-bold text-slate-400 mt-1 uppercase">{{ formatDate(submissionData.submittedAt) }}</span>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { 
  CalendarDaysIcon, ShieldCheckIcon, CheckCircleIcon, 
  CheckBadgeIcon 
} from '@heroicons/vue/24/outline';

defineProps({
  submissionData: { type: Object, required: true },
  formatDate: { type: Function, required: true }
});

const agreements = [
  { label: 'Code of Conduct' },
  { label: 'Company Policies' },
  { label: 'Company Procedures' },
  { label: 'Primary Sponsor Contract' },
  { label: 'Safety Rules Acknowledgement' },
  { label: 'Receipt of Information' }
];
</script>

<style scoped>
.StatCard { @apply flex flex-col p-6 rounded-[28px] border border-slate-100 shadow-sm; }
.StatCard .label { @apply text-[9px] font-black uppercase text-slate-400 tracking-widest mb-1; }
.StatCard .value { @apply text-sm font-black text-[#0A1630] truncate; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>